import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Trash2 } from "lucide-react";
import { Toast } from "../../components/Toast";

type TextStats = {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  sentences: number;
  paragraphs: number;
  readingTime: number;
};

const analyzeText = (text: string): TextStats => {
  if (!text.trim()) {
    return {
      words: 0,
      characters: 0,
      charactersNoSpaces: 0,
      sentences: 0,
      paragraphs: 0,
      readingTime: 0,
    };
  }

  const characters = text.length;
  const charactersNoSpaces = text.replace(/\s/g, "").length;

  // Count words: split by whitespace, filter empty strings
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length;

  // Count sentences: split by sentence-ending punctuation
  const sentences = text
    .split(/[.!?]+/)
    .filter((s) => s.trim().length > 0).length;

  // Count paragraphs: split by double newlines or more
  const paragraphs = text
    .split(/\n\s*\n/)
    .filter((p) => p.trim().length > 0).length || (text.trim() ? 1 : 0);

  // Reading time: average 200 words per minute
  const readingTime = Math.max(1, Math.ceil(words / 200));

  return {
    words,
    characters,
    charactersNoSpaces,
    sentences,
    paragraphs,
    readingTime,
  };
};

export function WordCounter() {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const stats = useMemo(() => analyzeText(text), [text]);

  const handleClear = useCallback(() => {
    setText("");
  }, []);

  const handleCopyStats = useCallback(async () => {
    const statsText = `${t("wordCounter.words")}: ${stats.words}
${t("wordCounter.characters")}: ${stats.characters}
${t("wordCounter.charactersNoSpaces")}: ${stats.charactersNoSpaces}
${t("wordCounter.sentences")}: ${stats.sentences}
${t("wordCounter.paragraphs")}: ${stats.paragraphs}
${t("wordCounter.readingTime")}: ~${stats.readingTime} ${t("wordCounter.minutes")}`;

    try {
      await navigator.clipboard.writeText(statsText);
      setToastMessage(t("wordCounter.copied"));
    } catch {
      setToastMessage(t("wordCounter.copyFailed"));
    }
  }, [stats, t]);

  const statItems = [
    { label: t("wordCounter.words"), value: stats.words },
    { label: t("wordCounter.characters"), value: stats.characters },
    { label: t("wordCounter.charactersNoSpaces"), value: stats.charactersNoSpaces },
    { label: t("wordCounter.sentences"), value: stats.sentences },
    { label: t("wordCounter.paragraphs"), value: stats.paragraphs },
    {
      label: t("wordCounter.readingTime"),
      value: `~${stats.readingTime} ${t("wordCounter.minutes")}`,
    },
  ];

  return (
    <div className="word-counter">
      <div className="wc-input-section">
        <textarea
          className="wc-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("wordCounter.placeholder")}
        />
      </div>

      <div className="wc-stats-grid">
        {statItems.map((item) => (
          <div key={item.label} className="wc-stat-card">
            <span className="wc-stat-value">{item.value}</span>
            <span className="wc-stat-label">{item.label}</span>
          </div>
        ))}
      </div>

      <div className="wc-actions">
        <button
          type="button"
          className="wc-secondary-btn"
          onClick={handleClear}
          disabled={!text}
        >
          <Trash2 size={16} />
          {t("wordCounter.clear")}
        </button>
        <button
          type="button"
          className="wc-secondary-btn"
          onClick={handleCopyStats}
          disabled={!text}
        >
          <Copy size={16} />
          {t("wordCounter.copyStats")}
        </button>
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
